import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  RUNBOOK_STEP_SSE_EVENT,
  type StepEventDTO,
  type StepEventSseEnvelope,
} from '@deepguide-ai/types'
import { SERVER_URL } from '@/lib/env'
import { trpc } from '@/lib/trpc'

export type { StepEventDTO as StepEvent, StepEventPayload } from '@deepguide-ai/types'

interface UseStepEventStreamOptions {
  stepRunId: string | null
  executionId: string | null
  /** Stop streaming once the step has finalized — backfill stays cached. */
  enabled?: boolean
}

/**
 * Subscribes to per-step tool-call events for a runbook step run:
 * tRPC backfill so re-entering a finished step still shows the
 * timeline, plus a tail of the existing SSE channel deduped by `seq`.
 * On reconnect, re-running the tRPC query with `sinceSeq` would catch
 * up — for V1 we just refetch the full window via cache invalidation.
 */
export function useStepEventStream({
  stepRunId,
  executionId,
  enabled = true,
}: UseStepEventStreamOptions): {
  events: StepEventDTO[]
  isLoading: boolean
} {
  const qc = useQueryClient()
  const [liveBySeq, setLiveBySeq] = useState<Map<number, StepEventDTO>>(
    () => new Map(),
  )

  const query = useQuery({
    ...trpc.runbook.listStepEvents.queryOptions({ stepRunId: stepRunId ?? '' }),
    enabled: !!stepRunId && enabled,
  })

  useEffect(() => {
    setLiveBySeq(new Map())
  }, [stepRunId])

  useEffect(() => {
    if (!stepRunId || !executionId || !enabled) return

    const queryKey = trpc.runbook.listStepEvents.queryKey({ stepRunId })
    const es = new EventSource(
      `${SERVER_URL}/api/runbook-executions/${executionId}/events`,
      { withCredentials: true },
    )

    const handler = (event: MessageEvent<string>) => {
      try {
        const envelope = JSON.parse(event.data) as StepEventSseEnvelope
        if (envelope.stepRunId !== stepRunId) return
        if (typeof envelope.seq !== 'number') return
        setLiveBySeq((prev) => {
          if (prev.has(envelope.seq)) return prev
          const next = new Map(prev)
          next.set(envelope.seq, {
            id: `live-${envelope.seq}`,
            stepRunId: envelope.stepRunId,
            seq: envelope.seq,
            payload: envelope.payload,
            createdAt: new Date(envelope.emittedAt),
          })
          return next
        })
      } catch {
        /* ignore malformed event */
      }
    }

    es.addEventListener(RUNBOOK_STEP_SSE_EVENT, handler as EventListener)
    es.addEventListener('error', () => {
      if (es.readyState === EventSource.CLOSED) return
      void qc.invalidateQueries({ queryKey })
    })

    return () => {
      es.removeEventListener(RUNBOOK_STEP_SSE_EVENT, handler as EventListener)
      es.close()
    }
  }, [stepRunId, executionId, enabled, qc])

  const events = useMemo(() => {
    const merged = new Map<number, StepEventDTO>()
    for (const evt of (query.data ?? []) as StepEventDTO[]) {
      merged.set(evt.seq, evt)
    }
    for (const [seq, evt] of liveBySeq) {
      if (!merged.has(seq)) merged.set(seq, evt)
    }
    return [...merged.values()].sort((a, b) => a.seq - b.seq)
  }, [query.data, liveBySeq])

  return {
    events,
    isLoading: query.isLoading && !!stepRunId,
  }
}
